import { getCurrentLocale } from "./locale";
import { SortingFn } from "@tanstack/react-table";
import useBoundStore from "@/services/store/BoundStore";
import { round } from "lodash";
import {
  DEFAULT_CURRENCY_CODE,
  formatNumber as formatNumberInCurrency,
  formatPercentage as formatPercentageInLocale
} from "./numberFormat";

// A wrapper rather than a re-export: re-exporting an imported binding makes the
// fast-refresh lint rule treat this file as exporting components.
export const formatPercentage = (
  ...args: Parameters<typeof formatPercentageInLocale>
): string => formatPercentageInLocale(...args);

/** The value an account setting holds; every setting key shares this union. */
export type AccountSettingValue = string | boolean | null;

/** A value that resolved to a usable string, as opposed to absent or empty. */
const isResolvedValue = (value: unknown): value is string =>
  typeof value === "string" && value !== "";

/**
 * Locale a caller passes to formatMonetaryDisplayValue when the string it gets
 * back will be parsed again, by a parse side that recognises a comma as the
 * grouping separator and a dot as the decimal one.
 *
 * A round trip has to pin a locale rather than follow the viewer's: in a region
 * that groups with a dot (de-DE, nl-NL, it-IT, pt-BR, ...) the viewer's own
 * format turns 2500 into "2.500", which the parser then reads as the decimal
 * 2.5 — so typing 25000 saved 2.50. Formatting and parsing have to agree on one
 * convention.
 *
 * What earns the pin is being parsed back, not being editable. A field the user
 * is typing into qualifies; the collapsed text that same field renders when it
 * is not being edited does not, and follows the viewer's locale like any other
 * display surface.
 */
export const ROUND_TRIP_NUMBER_LOCALE = "en-US";

// Intl names the separator for us; a locale with no decimal part in its
// pattern (none in practice) falls back to the dot every parse site expects.
const getDecimalSeparator = (locale: string): string =>
  new Intl.NumberFormat(new Intl.Locale(locale))
    .formatToParts(1.1)
    .find((part) => part.type === "decimal")?.value ?? ".";

/**
 * Reads what the viewer typed into a money field and re-expresses it on the
 * canonical convention (no grouping, dot decimal) the API and every parse site
 * expect. NOT a validator: a separator-only entry comes back as "." or "-.",
 * which is not a number, so callers keep their own numeric guard rather than
 * treating a returned string as parseable.
 *
 * Separator COUNT decides how much the locale matters, and only a lone comma is
 * genuinely locale-dependent. In German, French, Spanish, Italian, Dutch and
 * Portuguese the comma is the decimal separator, so stripping it unconditionally
 * turned 25000,50 into 2500050 — a hundred times the amount, stored with no
 * error. Where the locale's decimal separator is the dot, that same
 * lone comma is grouping and stripping it is right: reading it as a decimal
 * would turn a US user's 25,000 into 25.
 *
 * The DOT is always the decimal separator, in every locale, and is deliberately
 * never treated as grouping even where the locale groups with it. It is the
 * canonical form the value is stored and re-read in, so a typed dot has to
 * survive a round trip under every viewer — "keeps an explicitly typed decimal"
 * in the locale suite. Stripping it by locale breaks that silently: 1234.56
 * typed in German becomes 123456.
 *
 * That rule is also what makes this safe to call on its own output, which it is:
 * `handleChange` re-parses the whole box every keystroke, and the box holds the
 * canonical dot this function just wrote. Reading a lone dot by locale instead
 * made the field misread its own write-back — a German typing 0,125 got 125 the
 * moment the third decimal digit landed. Any future attempt to read grouping
 * here must first distinguish a separator the field produced from one the user
 * just typed; this function cannot see the difference.
 *
 * So one residual ambiguity survives: a SINGLE dot in a comma-decimal locale. A
 * German typing "25.000" for twenty-five thousand gets 25, because that string
 * is indistinguishable from twenty-five and nothing here can separate them.
 */
// Spaced like real grouping: 1-3 leading digits, then groups of exactly three.
// Only the several-separator branch uses it — a lone dot is always the decimal.
const GROUPED_SHAPE = /^-?\d{1,3}(?:[.,]\d{3})+$/;

export const parseLocaleNumericInput = (
  input: string,
  locale: string = getCurrentLocale()
): string => {
  const separatorCount = (input.match(/[.,]/g) ?? []).length;
  if (separatorCount === 0) return input;

  // Two or more separators need no locale: a number carries at most one decimal
  // separator, so with several of them the pattern alone settles the roles.
  if (separatorCount > 1) {
    // All the same character AND spaced like real grouping — every group after
    // the first exactly three digits — means none of them is a decimal.
    // "1,200,000" and German "1.200.000" are both 1200000; reading the last as a
    // decimal divides by a thousand, which is how an earlier version of this
    // turned 1,200,000 into 1200.
    //
    // The shape has to be checked, not assumed. "1.200.000.55" uses the dot for
    // grouping AND as the decimal — malformed, but typeable, and a numpad
    // decimal key emits it. Stripping it wholesale gives 120000055, a hundred
    // times the amount. Its last group is two digits, so it falls through to the
    // rule below and reads correctly as 1200000.55.
    const distinct = new Set(input.match(/[.,]/g));
    if (distinct.size === 1 && GROUPED_SHAPE.test(input)) {
      return input.replace(/[.,]/g, "");
    }

    // Otherwise the last separator is the decimal and the rest are grouping.
    // For mixed separators that is exact — the other character is doing the
    // grouping, so "1,234,567.89" and "25.000,50" both come back correct
    // whichever convention they were written in. For a malformed same-character
    // value it is the reading that recovers the amount the user meant.
    const decimalAt = Math.max(input.lastIndexOf("."), input.lastIndexOf(","));
    const integerPart = input.slice(0, decimalAt).replace(/[.,]/g, "");
    return `${integerPart}.${input.slice(decimalAt + 1)}`;
  }

  // Exactly one separator is the genuinely ambiguous case, and only here does
  // the locale decide. A dot is always the decimal point — canonical, and the
  // only reading that survives this function being run on its own output. A
  // comma is the decimal point only where the locale says so, and grouping
  // otherwise: reading it as a decimal would turn a US user's 25,000 into 25.
  if (input.includes(".")) return input;
  return getDecimalSeparator(locale) === ","
    ? input.replace(",", ".")
    : input.replace(",", "");
};

/**
 * The currency code held by an account setting, or null.
 *
 * `AccountSettingValue` is `string | boolean | null` — one
 * union covering every setting key — so reading a currency out of it is a
 * narrowing, not a cast. Written once and shared because an `as string` here
 * does not merely lie to the compiler: `(value as string) || null` passes a
 * boolean `true` straight through, and a `true` reaching Intl raises
 * `RangeError: Invalid currency code` from an unguarded `formatNumber`, while
 * one reaching `systemCurrencyHeader` renders the column header `ARR (true)`.
 */
export const currencyCodeFromSetting = (
  value: AccountSettingValue | undefined
): string | null => (isResolvedValue(value) ? value : null);

/** The account's currency as the store holds it, or the default before it loads. */
export const selectAccountCurrency = (
  state: ReturnType<typeof useBoundStore.getState>
): string =>
  currencyCodeFromSetting(
    state.settings.accountSettings["account_currency"]?.value
  ) ?? DEFAULT_CURRENCY_CODE;

const getAccountCurrency = (): string =>
  selectAccountCurrency(useBoundStore.getState());

export function formatNumber(
  num: number,
  precision: number = 1,
  isCurrency: boolean = false,
  compact: boolean = true,
  minPrecision?: boolean,
  locale: string = getCurrentLocale(),
  isPretty: boolean = true,
  currencyCode?: string
): { value: string; modifier: string; currencySign?: string } {
  return formatNumberInCurrency(
    num,
    precision,
    isCurrency,
    compact,
    minPrecision,
    locale,
    isPretty,
    // Resolved only when it is needed, as before the split.
    isCurrency ? currencyCode || getAccountCurrency() : currencyCode
  );
}

function isInvalidNumericValue(num: unknown): boolean {
  return (
    num === null ||
    num === undefined ||
    num === "" ||
    (typeof num === "number" && Number.isNaN(num))
  );
}

export function displayNumberOrFallback(
  num: number | null | undefined,
  fallback: string
): string | number {
  return isInvalidNumericValue(num) ? fallback : (num as number);
}

export function formatNumberOrFallback(
  num: number | null | undefined,
  fallback: string,
  precision: number = 1,
  isCurrency: boolean = false,
  compact: boolean = true,
  minPrecision?: boolean,
  locale: string = getCurrentLocale(),
  isPretty: boolean = true,
  currencyCode?: string
): string {
  if (isInvalidNumericValue(num)) {
    return fallback;
  }

  return formatNumber(
    num as number,
    precision,
    isCurrency,
    compact,
    minPrecision,
    locale,
    isPretty,
    currencyCode
  ).value;
}

export function displayTotalCurrency(
  total: number | undefined,
  minPrecision?: boolean,
  locale: string = getCurrentLocale(),
  currencyCode?: string,
  // Replaces only the sign, never the figure: the fraction digits Intl applies
  // are the currency's own and stay tied to `currencyCode`. Named for what it
  // does to keep it distinct from formatNumber's own `currencySign` result,
  // which it stands in for on the line below.
  signOverride?: string
) {
  const totalFormatted = formatNumber(
    total ?? 0,
    minPrecision ? 1 : 0,
    true,
    false,
    minPrecision,
    locale,
    true,
    currencyCode
  );
  return `${signOverride ?? totalFormatted.currencySign}${totalFormatted.value}`;
}

const VALUES_NUMERIC_PRECISION = 2;

export const reducePrecision = (
  num: number,
  precision: number = VALUES_NUMERIC_PRECISION
): number => round(num, precision);

/**
 * Percent values are stored as fractions (0.3 = 30%). UI inputs always work
 * in 1–100 space — these helpers do the round-trip without scattering
 * ×100 / ÷100 arithmetic across the codebase.
 */
export const toPercentInput = (
  value: number | string | null | undefined
): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return null;
  return round(num * 100, 10);
};

export const fromPercentInput = (
  value: number | string | null | undefined
): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return null;
  return round(num / 100, 10);
};

export const numberSortingFn: SortingFn<any> = (rowA, rowB, columnId) => {
  const aValue = rowA.getValue(columnId) as number;
  const bValue = rowB.getValue(columnId) as number;
  return aValue - bValue;
};

export interface MonetaryDisplayOptions {
  isMonetary?: boolean;
  isPretty?: boolean;
  currencySymbol?: string;
  /**
   * Drop the sign and return the grouped digits alone. For a value rendered
   * inside an input: the field's guard admits digits, commas and dots only, so
   * a sign in the value is rejected on the next keystroke.
   */
  omitCurrencySign?: boolean;
  /**
   * Render the digits ungrouped. For a value inside an input the user is
   * typing into: grouping the box made a typed separator ambiguous, because the
   * field wrote separators of its own into the same string the parse then had to
   * interpret. Dropping them narrows that to the one separator the field still
   * writes — the canonical decimal point — which is why
   * `parseLocaleNumericInput` reads a lone dot positionally and only a comma by
   * locale. The grouped form returns the moment the field is no longer being
   * edited, which is what `getMonetaryDisplayValue` renders.
   */
  omitGrouping?: boolean;
  /**
   * Resolve the sign for this currency instead of reading the account's. Ties
   * the sign to a value the caller already watches; omitted, the account
   * currency is read from the store, which is the same answer by construction
   * but not one a caller can depend on re-rendering for.
   *
   * `currencySymbol` wins over this. A surface showing a record's own currency
   * rather than the account's — contract terms, say — wants `currencySymbol`
   * from `getTermsCurrencySign`, which disambiguates a sign that collides with
   * the account's. Setting `currencyCode` there instead renders a sign that
   * skipped that step.
   */
  currencyCode?: string;
}

export const getCurrencySign = (
  {
    isMonetary = false,
    isPretty = false,
    currencySymbol,
    omitCurrencySign = false,
    currencyCode
  }: MonetaryDisplayOptions,
  locale: string
): string => {
  if (!isMonetary || omitCurrencySign) return "";
  if (currencySymbol) return currencySymbol;
  return (
    formatNumber(
      0,
      0,
      true,
      false,
      false,
      locale,
      isPretty || isMonetary,
      currencyCode
    ).currencySign || ""
  );
};

/**
 * `narrowSymbol` is the bare glyph — the one `getCurrencySign` resolves for the
 * account's own currency, and the one many currencies share. `symbol` is
 * Intl's qualified form, which names the currency where its locale needs the
 * two told apart: under en-US, CAD narrows to `$` and qualifies to `CA$`.
 *
 * Not a length ordering, so not a "width": RON is `lei` narrow and `RON`
 * qualified, and a locale's own currency is the same string at both.
 */
type CurrencySignForm = "narrowSymbol" | "symbol";

// The answer is fixed for a given locale, code and form, and resolving it is
// almost entirely the cost of building the formatter. A terms table column
// asks per row, for a handful of distinct currencies — so cache rather than
// rebuild an Intl.NumberFormat per cell.
const currencySignCache = new Map<string, string>();

// Intl's symbol for an explicit code — the sign getCurrencySign resolves for the account's own currency.
export const getCurrencySignForCode = (
  currencyCode: string,
  form: CurrencySignForm = "narrowSymbol"
): string => {
  const locale = getCurrentLocale();
  // Upper-cased: Intl is case-insensitive for currency codes, so `usd` and
  // `USD` resolve identically and must not occupy two entries.
  const cacheKey = `${locale}\u0000${form}\u0000${String(currencyCode).toUpperCase()}`;
  const cached = currencySignCache.get(cacheKey);
  if (cached !== undefined) return cached;

  let sign: string;
  try {
    // Built here rather than through formatNumber, which fixes currencyDisplay at narrowSymbol; the currency part is the same either way.
    sign =
      new Intl.NumberFormat(new Intl.Locale(locale), {
        style: "currency",
        currency: currencyCode,
        currencyDisplay: form
      })
        .formatToParts(0)
        .find((part) => part.type === "currency")?.value ||
      // The fallback only makes the `string` return hold against an optional part; Intl never leaves it empty for a code it accepts.
      currencyCode;
  } catch (error) {
    // Intl raises RangeError on a code that is not three letters, and a contract's currency arrives from the API.
    if (!(error instanceof RangeError)) throw error;
    sign = currencyCode;
  }

  // Cache only a real resolution. Intl accepts exactly three ASCII letters and
  // raises RangeError on everything else, so a code of that shape is the only
  // one that ever produced an answer — which also bounds the cache by ISO-4217
  // rather than by whatever the API sends. Anything else takes the same
  // uncached path it took before this cache existed.
  if (typeof sign === "string" && /^[A-Za-z]{3}$/.test(currencyCode)) {
    currencySignCache.set(cacheKey, sign);
  }
  return sign;
};

interface AdjacentCurrencySigns {
  /**
   * The sign the surface would otherwise print for `currencyCode`. Optional
   * because `getCurrencySymbol` answers undefined for a currency its package
   * does not carry, and surfaces pass that straight through — the return type
   * says so rather than letting a `string` annotation cover for it.
   */
  sign: string | undefined;
  currencyCode: string | null | undefined;
  /** The sign the same surface prints for the account's own currency beside it. */
  accountSign: string | undefined;
  accountCurrencyCode: string | null | undefined;
}

/**
 * Whether a record's figure needs a converted one shown beside it — i.e.
 * whether its currency genuinely differs from the account's.
 *
 * Shared rather than re-derived, because it has to agree with
 * `distinguishCurrencySign` above on two things at once: an unresolved account
 * currency is *unknown*, not "different", so nothing converts until it lands;
 * and codes are compared case-folded, so a record in `usd` on a `USD` account
 * is the same currency. Three surfaces hand-rolled this rule and drifted — one
 * kept a raw `!==`, so its sign said "same currency" while its converted
 * figure said "different", and two called `.toUpperCase()` on values the
 * account-settings union permits to be `null` or `false`.
 */
export const isCurrencyConversionNeeded = (
  // Wide enough for the account-settings union that reaches here, narrow
  // enough that a number or an object is rejected at compile time rather than
  // silently answering false at runtime.
  accountCurrencyCode: string | boolean | null | undefined,
  recordCurrencyCode: string | null | undefined
): boolean =>
  isResolvedValue(accountCurrencyCode) &&
  isResolvedValue(recordCurrencyCode) &&
  accountCurrencyCode.toUpperCase() !== recordCurrencyCode.toUpperCase();

/**
 * Each side must be passed the sign that side actually renders — not both from
 * one resolver. The test compares glyphs, so what matters is the two glyphs the
 * user sees, whichever resolver produced each.
 *
 * That distinction is load-bearing because the app has three currency-symbol
 * sources and they genuinely disagree: `currency-list`'s `symbol_native`
 * answers a fullwidth `￥` for JPY and a Bengali string for INR where Intl
 * answers `¥` and `₹`. Where both halves happen to render through one resolver
 * the rule collapses to "same resolver" — the KPI header resolves both through
 * `getCurrencySymbol`, the Details rows and table cells both through
 * `getCurrencySignForCode`. Where they do not, passing one resolver's sign for
 * both is the bug: the ContractTermsV2 surfaces print the record half with
 * `formatCurrency` (Intl) beside an account half from `getCurrencySymbol`
 * (currency-list), so the hooks deliberately pass signs from two different
 * sources. Seeding both from the account side there silently replaced every
 * non-colliding record sign with its currency-list spelling.
 */
/**
 * A figure in a record's own currency is shown beside one in the account's all
 * over the app — the Details rows, the header's converted amount, the paired
 * table columns. Narrow symbols are not unique: `$` is USD, CAD, AUD, NZD,
 * HKD, SGD, MXN and more, so the pair renders as two different numbers under
 * one glyph with nothing to say which is which.
 *
 * Where the two signs collide, the record's takes Intl's qualified form, which
 * names it (`CA$`). Where they already differ the sign is returned untouched,
 * so every currency that never had the problem — and any symbol a field
 * configures for itself — renders exactly as before.
 */
export const distinguishCurrencySign = ({
  sign,
  currencyCode,
  accountSign,
  accountCurrencyCode
}: AdjacentCurrencySigns): string | undefined => {
  // All four are `string` by annotation only: the codes arrive from the API
  // and the account-settings query, and `getCurrencySymbol` answers undefined
  // for a currency its package does not carry (FJD, XCD). Anything unresolved
  // means there is nothing to tell apart, so the surface keeps what it would
  // have printed — which also keeps `.toUpperCase()` below off a non-string,
  // where it would throw rather than fall back the way Intl does, and stops
  // two *unresolved* signs from comparing equal and reading as a collision.
  if (
    !isResolvedValue(currencyCode) ||
    !isResolvedValue(accountCurrencyCode) ||
    !isResolvedValue(sign) ||
    !isResolvedValue(accountSign)
  ) {
    return sign;
  }
  // One glyph standing for one currency is not ambiguous, however the two
  // sides spell the code.
  if (currencyCode.toUpperCase() === accountCurrencyCode.toUpperCase()) {
    return sign;
  }
  if (sign !== accountSign) return sign;

  const qualified = getCurrencySignForCode(currencyCode, "symbol");
  // Intl qualifies a currency against its own locale, not against the account:
  // under en-US the locale's own USD stays `$` at both widths, so a USD figure
  // on a CAD account would collide a second time. The code always
  // distinguishes, so it is the last resort rather than another collision.
  return qualified === accountSign ? currencyCode : qualified;
};

/**
 * The sign to print for `currencyCode` with the account's own currency beside
 * it, for a surface that has no sign of its own in hand.
 */
export const getTermsCurrencySign = (
  currencyCode: string,
  accountCurrencyCode: string | null | undefined
): string => {
  // Always a real string here — getCurrencySignForCode falls back to the code
  // itself — so this caller can promise one where the shared helper cannot.
  const sign = getCurrencySignForCode(currencyCode);
  return (
    distinguishCurrencySign({
      sign,
      currencyCode,
      accountSign: accountCurrencyCode
        ? getCurrencySignForCode(accountCurrencyCode)
        : "",
      accountCurrencyCode
    }) ?? sign
  );
};

/**
 * Renders a monetary figure for display. The incoming string is always in the
 * canonical form the API and the input round trip use — comma grouping, dot
 * decimal — while the string handed back is grouped and punctuated for
 * `locale`, so a read-only surface reads naturally to whoever is looking at it.
 *
 * A caller whose output gets parsed again passes ROUND_TRIP_NUMBER_LOCALE, which
 * renders in the one convention every parse site understands.
 */
export const formatMonetaryDisplayValue = (
  value: string,
  options: MonetaryDisplayOptions,
  locale: string = getCurrentLocale()
): string => {
  const {
    isMonetary = false,
    isPretty = false,
    omitGrouping = false
  } = options;
  // One decision, applied at all three formatNumber calls below, so the input
  // and display paths cannot drift apart on grouping.
  const useGrouping = (isPretty || isMonetary) && !omitGrouping;
  if (!value || value === "") return "";

  const isLikelyPhoneNumber =
    value.startsWith("+") || /^\d{3,}-?\d+/.test(value);
  if (isLikelyPhoneNumber && !isMonetary) return value;

  const normalizedValue = value.replace(/,/g, "");
  // At most one decimal separator. A looser test let "1.2.3" and "10.0.0.1"
  // through, and the two-element destructuring below drops everything after the
  // second separator — rendering them as "1.2" and "10.0" rather than leaving
  // the value alone.
  const isNumeric = /^-?(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalizedValue);
  if (!isNumeric) return value;

  const currencySign = getCurrencySign(options, locale);
  const decimalSeparator = getDecimalSeparator(locale);

  if (normalizedValue.includes(".")) {
    const [integerPart, decimalPart] = normalizedValue.split(".");
    if (integerPart === "") {
      return `${currencySign}${value}`;
    } else if (decimalPart.length > 2) {
      // API-computed values (ACV/ARR/MRR division results) can carry long
      // float tails — round to 2 decimals (with carry) instead of the
      // raw-passthrough below, which only applies while a user is
      // actively typing 1-2 decimal digits. Round through a fixed "." locale
      // so the split below always finds the fraction; `locale` punctuates the
      // pieces once they are apart.
      const numericValue = parseFloat(normalizedValue);
      if (!isNaN(numericValue)) {
        const roundedStr = numericValue.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          useGrouping: false
        });
        // Rounding a small negative value (e.g. -0.001) to 2 decimals can
        // land exactly on zero — strip the sign so it doesn't render as
        // "-0.00", matching how a genuinely zero value would display.
        const normalizedStr =
          parseFloat(roundedStr) === 0
            ? roundedStr.replace(/^-/, "")
            : roundedStr;
        const [roundedInteger, roundedDecimal] = normalizedStr.split(".");
        const { value: formattedInteger } = formatNumber(
          parseFloat(roundedInteger),
          0,
          false,
          false,
          false,
          locale,
          useGrouping
        );
        return `${currencySign}${formattedInteger}${decimalSeparator}${roundedDecimal}`;
      }
    } else {
      // "-.5" splits to a bare "-" integer part, which parseFloat reads as NaN
      // and would drop the value out of formatting entirely.
      const numericValue = parseFloat(integerPart === "-" ? "-0" : integerPart);
      if (!isNaN(numericValue)) {
        const { value: formattedInteger } = formatNumber(
          numericValue,
          0,
          false,
          false,
          false,
          locale,
          useGrouping
        );
        return `${currencySign}${formattedInteger}${decimalSeparator}${decimalPart}`;
      }
    }
  } else {
    const numericValue = parseFloat(normalizedValue);
    if (!isNaN(numericValue)) {
      const { value: formattedValue } = formatNumber(
        numericValue,
        0,
        false,
        false,
        false,
        locale,
        useGrouping
      );
      return `${currencySign}${formattedValue}`;
    }
  }

  return value;
};
